import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[chrona-pro] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is not set — ' +
      'auth will not work. Copy .env.example to .env.local and fill in values.',
  )
}

/**
 * Snapshot the password-recovery tokens from the URL hash BEFORE
 * `createClient` runs (createClient schedules an async `_initialize()`
 * that consumes and clears `window.location.hash`).
 *
 * Why: Supabase's `_getSessionFromURL` internally calls `_getUser()` before
 * saving the session. If that call fails (transient network, rate limit, etc.)
 * the session is never saved and `PASSWORD_RECOVERY` is never fired. Without
 * this snapshot, `UpdatePasswordPage.submit` → `updateUser` → `_useSession`
 * returns null and the user sees "reset link may have expired."
 *
 * With the snapshot, `UpdatePasswordPage` can call `setSession(RECOVERY_HASH_TOKENS)`
 * as a second-chance before `updateUser` — not coupled to the SDK's one-shot init.
 */
export const RECOVERY_HASH_TOKENS: {
  access_token: string
  refresh_token: string
} | null = (() => {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  if (!hash.startsWith('#')) return null
  const params = new URLSearchParams(hash.slice(1))
  const access_token = params.get('access_token')
  const refresh_token = params.get('refresh_token')
  const type = params.get('type')
  // Only snapshot for recovery — avoid capturing signup / magic-link / OAuth
  // hash tokens that the normal SDK init path handles correctly.
  if (access_token && refresh_token && type === 'recovery') {
    return { access_token, refresh_token }
  }
  return null
})()

// Pin flowType to 'implicit' so a future @supabase/supabase-js bump doesn't
// silently flip the default to PKCE (PKCE delivers ?code= query params instead
// of #access_token= hash fragments, and nothing here calls exchangeCodeForSession).
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { flowType: 'implicit' },
})
